/**
 * Utility functions for exporting data
 */

/**
 * Converts table data to CSV format
 * @param columns - Array of column definitions with key and label
 * @param rows - Array of row data objects
 * @returns CSV string
 */
export const tableToCSV = (
  columns: Array<{ key: string; label: string | React.ReactNode }>,
  rows: Array<Record<string, any>>,
): string => {
  // Extract column labels (handling React nodes)
  const headers = columns.map((col) => {
    if (typeof col.label === 'string') {
      return col.label;
    }
    // If label is a React node, try to extract text from it
    return col.key;
  });

  // Create CSV header row
  const csvHeaders = headers.map((h) => `"${h}"`).join(',');

  // Create CSV data rows
  const csvRows = rows.map((row) => {
    return columns
      .map((col) => {
        const value = row[col.key];

        // Handle React nodes by extracting text content
        if (typeof value === 'object' && value !== null) {
          // Try to extract text from React element
          if (value.props?.children) {
            const extractText = (node: any): string => {
              if (typeof node === 'string' || typeof node === 'number') {
                return String(node);
              }
              if (Array.isArray(node)) {
                return node.map(extractText).join(' ');
              }
              if (node?.props?.children) {
                return extractText(node.props.children);
              }
              return '';
            };
            return `"${extractText(value.props.children).replace(/"/g, '""')}"`;
          }
          return '""';
        }

        // Handle null/undefined
        if (value === null || value === undefined) {
          return '""';
        }

        // Handle strings and numbers
        return `"${String(value).replace(/"/g, '""')}"`;
      })
      .join(',');
  });

  return [csvHeaders, ...csvRows].join('\n');
};

/**
 * Downloads a string as a file
 * @param content - File content
 * @param filename - Name of the file to download
 * @param mimeType - MIME type of the file
 */
export const downloadFile = (
  content: string,
  filename: string,
  mimeType: string = 'text/csv;charset=utf-8;',
): void => {
  const blob = new Blob([content], { type: mimeType });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

/**
 * Creates a zip file with multiple CSV files
 * @param files - Array of {filename, content} objects
 * @returns Promise that resolves when download starts
 */
export const downloadZipFile = async (
  files: Array<{ filename: string; content: string }>,
  zipFilename: string,
): Promise<void> => {
  // Dynamic import of JSZip to avoid bundling if not used
  const JSZip = (await import('jszip')).default;

  const zip = new JSZip();

  // Add each file to the zip
  files.forEach(({ filename, content }) => {
    zip.file(filename, content);
  });

  // Generate zip file
  const zipBlob = await zip.generateAsync({ type: 'blob' });

  // Download the zip
  const link = document.createElement('a');
  const url = URL.createObjectURL(zipBlob);

  link.setAttribute('href', url);
  link.setAttribute('download', zipFilename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
};

/**
 * Formats a date for use in filenames
 * @param date - Date to format
 * @returns Formatted date string (YYYY-MM-DD)
 */
export const formatDateForFilename = (date: Date = new Date()): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Formats a date for display in exports
 * @param date - ISO date string
 * @returns Formatted date string (YYYY-MM-DD HH:MM)
 */
export const formatDateForDisplay = (date: string): string => {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

/**
 * Converts metric cards data to CSV format with date ranges as columns
 * @param metrics - Array of metric objects
 * @param startDate - Start date of the period
 * @param endDate - End date of the period
 * @param previousStartDate - Optional start date of comparison period
 * @param previousEndDate - Optional end date of comparison period
 * @returns CSV string
 */
export const metricsToCSV = (
  metrics: Array<{ label: string; value: string | number; previousValue?: string | number }>,
  startDate: string,
  endDate: string,
  previousStartDate?: string,
  previousEndDate?: string,
): string => {
  const hasPrevious = previousStartDate && previousEndDate;

  // Column headers with date ranges
  let csv = '';
  if (hasPrevious) {
    csv = '"Metric","Current Value","Previous Value","Current Period Start","Current Period End","Previous Period Start","Previous Period End"\n';
  } else {
    csv = '"Metric","Value","Period Start","Period End"\n';
  }

  // Data rows
  const formattedStartDate = formatDateForDisplay(startDate);
  const formattedEndDate = formatDateForDisplay(endDate);
  const formattedPrevStartDate = previousStartDate ? formatDateForDisplay(previousStartDate) : '';
  const formattedPrevEndDate = previousEndDate ? formatDateForDisplay(previousEndDate) : '';

  metrics.forEach((metric) => {
    const label = String(metric.label).replace(/"/g, '""');
    const value = String(metric.value).replace(/"/g, '""');

    if (hasPrevious) {
      const prevValue = metric.previousValue !== undefined ? String(metric.previousValue).replace(/"/g, '""') : '';
      csv += `"${label}","${value}","${prevValue}","${formattedStartDate}","${formattedEndDate}","${formattedPrevStartDate}","${formattedPrevEndDate}"\n`;
    } else {
      csv += `"${label}","${value}","${formattedStartDate}","${formattedEndDate}"\n`;
    }
  });

  return csv;
};
